import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PenLine, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { db } from "@/lib/db";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      return toast.error("As senhas não coincidem");
    }
    if (password.length < 6) {
      return toast.error("A senha deve ter pelo menos 6 caracteres");
    }

    setLoading(true);
    try {
      await db.resetPassword(token, password);
      setSuccess(true);
      toast.success("Senha alterada com sucesso!");
    } catch (err) {
      toast.error("Erro: " + (err.message || "Token inválido ou expirado"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-serif text-3xl mb-4 text-red-600">Link Inválido</h1>
          <p className="mb-6">O link de recuperação de senha parece estar incompleto ou expirado.</p>
          <Link to="/forgot-password">
            <Button variant="outline" className="rounded-none">Solicitar novo link</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center p-6 text-center">
        <div className="w-full max-w-md">
          <CheckCircle2 className="w-16 h-16 text-[#D9381E] mx-auto mb-6" />
          <h1 className="font-serif text-4xl mb-4">Senha redefinida!</h1>
          <p className="text-[#4A4A4A] mb-8 leading-relaxed">
            Sua senha foi alterada com sucesso. Você já pode entrar na sua conta.
          </p>
          <Link to="/login">
            <Button className="rounded-none w-full py-6 bg-[#D9381E] hover:bg-[#B82914]">
              Ir para o Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <PenLine className="w-5 h-5 text-[#D9381E]" strokeWidth={2.5} />
          <span className="font-serif text-2xl font-bold">RevisaPro</span>
        </div>

        <h1 className="font-serif text-4xl mb-2">Nova senha</h1>
        <p className="text-[#4A4A4A] mb-8">Escolha uma senha forte para proteger sua conta.</p>

        <form onSubmit={onSubmit} className="space-y-6 bg-white border border-black/10 p-8 shadow-editorial">
          <div>
            <Label htmlFor="pass" className="text-xs font-mono uppercase tracking-widest mb-2 block">Nova Senha</Label>
            <Input 
              id="pass" type="password" value={password} 
              onChange={(e) => setPassword(e.target.value)} required
              className="rounded-none border-black/20" 
              placeholder="******"
            />
          </div>

          <div>
            <Label htmlFor="confirm" className="text-xs font-mono uppercase tracking-widest mb-2 block">Confirmar Nova Senha</Label>
            <Input 
              id="confirm" type="password" value={confirmPassword} 
              onChange={(e) => setConfirmPassword(e.target.value)} required
              className="rounded-none border-black/20" 
              placeholder="******"
            />
          </div>
          
          <Button type="submit" disabled={loading}
            className="w-full rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white py-6 flex items-center justify-center gap-2">
            {loading ? "Salvando..." : <><Lock className="w-4 h-4" /> Redefinir senha</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
